import React ,{ useEffect, useMemo, useRef, useState } from "react";

import { decodeProject } from "../utils/serialization";
import { FormattedText } from "./FormattedText";
import { InteractionOverlay } from "./interactions/InteractionOverlay";
import  '../styles.css'




function isDragDropAnswer(value) {
  if (!value || typeof value !== "object" || !("assignments" in value)) {
    return false;
  }
  const assignments = (value).assignments;
  return (
    Array.isArray(assignments) &&
    assignments.every(
      (group) =>
        Array.isArray(group) &&
        group.every((itemIdx) => Number.isInteger(itemIdx))
    )
  );
}

function isWordBankFillAnswer(value) {
  if (!value || typeof value !== "object" || !("assignments" in value)) {
    return false;
  }
  const assignments = (value).assignments;
  return (
    Array.isArray(assignments) &&
    assignments.every((idx) => idx === null || Number.isInteger(idx))
  );
}

function isMultipleChoiceAnswer(value) {
  if (!value || typeof value !== "object" || !("selectedOptionIndexes" in value)) {
    return false;
  }
  const selected = (value ).selectedOptionIndexes;
  return Array.isArray(selected) && selected.every((idx) => Number.isInteger(idx));
}

function createEmptyMultipleChoiceAnswer() {
  return { selectedOptionIndexes: [] };
}

function isFillBlankAnswer(value) {
  if (!value || typeof value !== "object" || !("values" in value)) {
    return false;
  }
  const values = (value ).values;
  return Array.isArray(values) && values.every((v) => typeof v === "string");
}

function normalizeMultipleChoice(interaction) {
  if (Array.isArray(interaction.options) && interaction.options.length > 0) {
    return {
      selectionMode: interaction.selectionMode ?? "single",
      options: interaction.options.map((opt) => ({
        text: opt.text ?? "",
        isCorrect: Boolean(opt.isCorrect),
        tipText: opt.tipText ?? "",
        selectedFeedback: opt.selectedFeedback ?? "",
        unselectedFeedback: opt.unselectedFeedback ?? ""
      }))
    };
  }

  const legacyChoices = interaction.choices ?? [];
  const legacyCorrectIndex =
    typeof interaction.correctIndex === "number" ? interaction.correctIndex : 0;
  return {
    selectionMode: "single",
    options: legacyChoices.map((choice, idx) => ({
      text: choice,
      isCorrect: idx === legacyCorrectIndex,
      tipText: "",
      selectedFeedback: "",
      unselectedFeedback: ""
    }))
  };
}

function shuffleIndexes(length) {
  const indexes = Array.from({ length }, (_, i) => i);
  for (let i = indexes.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [indexes[i], indexes[j]] = [indexes[j], indexes[i]];
  }
  return indexes;
}

function createEmptyDragDropAnswer(
  interaction
) {
  return { assignments: Array.from({ length: interaction.slots.length }, () => []) };
}

function createEmptyWordBankFillAnswer(blankCount) {
  return { assignments: Array.from({ length: blankCount }, () => null) };
}

function toSafeDragDropAnswer(
  value,
  slotCount,
  itemCount
) {
  if (isDragDropAnswer(value)) {
    return {
      assignments: Array.from({ length: slotCount }, (_, slotIdx) => {
        const group = value.assignments[slotIdx] ?? [];
        return group.filter(
          (itemIdx, itemIdxPos, arr) =>
            Number.isInteger(itemIdx) &&
            itemIdx >= 0 &&
            itemIdx < itemCount &&
            arr.indexOf(itemIdx) === itemIdxPos
        );
      })
    };
  }
  return { assignments: Array.from({ length: slotCount }, () => []) };
}

function normalizeCorrectBySlot(interaction) {
  const raw = (interaction ).correctBySlot;
  if (!Array.isArray(raw)) {
    return Array.from({ length: interaction.slots.length }, () => []);
  }
  if (raw.length > 0 && typeof raw[0] === "number") {
    return (raw ).map((value) =>
      Number.isInteger(value) ? [value] : []
    );
  }
  return (raw ).map((group) =>
    Array.isArray(group)
      ? group.filter((value) => Number.isInteger(value))
      : []
  );
}

function correctAnswerLabel(interaction) {
  if (interaction.type === "multiple_choice") {
    const mc = normalizeMultipleChoice(interaction);
    return mc.options
      .filter((opt) => opt.isCorrect)
      .map((opt) => opt.text)
      .join(", ");
  }
  if (interaction.type === "true_false") {
    return interaction.correct ? "Doğru" : "Yanlış";
  }
  if (interaction.type === "drag_drop") {
    const correctBySlot = normalizeCorrectBySlot(interaction);
    return interaction.slots
      .map((slot, idx) => {
        const labels = (correctBySlot[idx] ?? [])
          .map((itemIdx) => interaction.items[itemIdx])
          .filter(Boolean)
          .join(", ");
        return `${slot}: ${labels}`;
      })
      .join(" | ");
  }
  if (interaction.type === "word_bank_fill") {
    return getWordBankCorrectWords(interaction).join(", ");
  }
  if (interaction.type === "fill_blank") {
    const fillData = parseFillBlankData(interaction);
    return fillData.blanks.map((answers) => answers[0] ?? "").join(", ");
  }
  return interaction.prompt;
}

function normalizeAnswerText(value){
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ");
}

function levenshteinDistance(a, b) {
  if (a === b) {
    return 0;
  }
  if (a.length === 0) {
    return b.length;
  }
  if (b.length === 0) {
    return a.length;
  }

  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  const curr = new Array(b.length + 1).fill(0);

  for (let i = 1; i <= a.length; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + cost
      );
    }
    for (let j = 0; j <= b.length; j += 1) {
      prev[j] = curr[j];
    }
  }

  return prev[b.length];
}

function isMinorSpellingMatch(candidate, target) {
  const distance = levenshteinDistance(candidate, target);
  const maxLen = Math.max(candidate.length, target.length);
  if (maxLen <= 4) {
    return distance <= 1;
  }
  if (maxLen <= 8) {
    return distance <= 2;
  }
  return distance <= 3;
}

function parseFillBlankData(interaction) {
  const source =
    (interaction.templateText && interaction.templateText.trim()) ||
    interaction.prompt;

  const markerRegex = /\*([^*]+)\*/g;
  const parts = [];
  const blanks = [];

  let cursor = 0;
  let blankIndex = 0;

  for (const match of source.matchAll(markerRegex)) {
    const full = match[0];
    const content = (match[1] || "").trim();
    const index = match.index || 0;

    if (index > cursor) {
      parts.push({ type: "text", value: source.slice(cursor, index) });
    }

    const answers = content
      .split("|")
      .map((v) => v.trim())
      .filter(Boolean);

    if (answers.length > 0) {
      blanks.push(answers);
      parts.push({ type: "blank", blankIndex });
      blankIndex += 1;
    } else {
      parts.push({ type: "text", value: full });
    }

    cursor = index + full.length;
  }

  if (cursor < source.length) {
    parts.push({ type: "text", value: source.slice(cursor) });
  }

  // Legacy fallback support
  if (blanks.length === 0) {
    const legacyAnswers = interaction.acceptedAnswers
      ? interaction.acceptedAnswers.filter(Boolean)
      : [];

    const tokenRegex = /_{3,}|\[blank\]/gi;
    let legacyCursor = 0;
    let legacyIdx = 0;

    parts.length = 0;

    for (const match of source.matchAll(tokenRegex)) {
      const full = match[0];
      const index = match.index || 0;

      if (index > legacyCursor) {
        parts.push({
          type: "text",
          value: source.slice(legacyCursor, index)
        });
      }

      const answers =
        legacyAnswers.length > 0 ? legacyAnswers : [""];

      blanks.push(answers);
      parts.push({ type: "blank", blankIndex: legacyIdx });

      legacyIdx += 1;
      legacyCursor = index + full.length;
    }

    if (legacyCursor < source.length) {
      parts.push({
        type: "text",
        value: source.slice(legacyCursor)
      });
    }
  }

  if (parts.length === 0) {
    parts.push({ type: "text", value: source });
  }

  return { parts, blanks };
}


function createEmptyFillBlankAnswer(interaction) {
  const fillData = parseFillBlankData(interaction);
  return { values: Array.from({ length: fillData.blanks.length }, () => "") };
}

function matchesFillBlankValue(candidateRaw, acceptedRawList) {
  const candidate = normalizeAnswerText(candidateRaw);
  if (!candidate) {
    return false;
  }
  return acceptedRawList.some((accepted) => {
    const normalizedAccepted = normalizeAnswerText(accepted);
    if (!normalizedAccepted) {
      return false;
    }
    if (normalizedAccepted === candidate) {
      return true;
    }
    return isMinorSpellingMatch(candidate, normalizedAccepted);
  });
}

function validateAnswer(
  interaction,
  answer
) {
  if (interaction.type === "multiple_choice") {
    if (!isMultipleChoiceAnswer(answer)) {
      return false;
    }
    const mc = normalizeMultipleChoice(interaction);
    const correctIndexes = mc.options
      .map((opt, idx) => (opt.isCorrect ? idx : -1))
      .filter((idx) => idx >= 0);
    const selected = Array.from(new Set(answer.selectedOptionIndexes)).sort(
      (a, b) => a - b
    );

    if (mc.selectionMode === "single") {
      return selected.length === 1 && correctIndexes[0] === selected[0];
    }

    const expected = [...correctIndexes].sort((a, b) => a - b);
    return (
      expected.length === selected.length &&
      expected.every((idx, pos) => idx === selected[pos])
    );
  }
  if (interaction.type === "true_false") {
    return Boolean(answer) === interaction.correct;
  }
  if (interaction.type === "drag_drop") {
    const correctBySlot = normalizeCorrectBySlot(interaction);
    if (!isDragDropAnswer(answer)) {
      return false;
    }
    if (answer.assignments.length !== correctBySlot.length) {
      return false;
    }
    return answer.assignments.every((assignedGroup, idx) => {
      const expected = [...(correctBySlot[idx] ?? [])].sort((a, b) => a - b);
      const actual = [...assignedGroup].sort((a, b) => a - b);
      return (
        expected.length === actual.length &&
        expected.every((value, valueIdx) => value === actual[valueIdx])
      );
    });
  }
  if (interaction.type === "word_bank_fill") {
    if (!isWordBankFillAnswer(answer)) {
      return false;
    }
    const expected = getWordBankCorrectWords(interaction);
    if (answer.assignments.length !== expected.length) {
      return false;
    }
    return answer.assignments.every((wordIdx, blankIdx) => {
      if (wordIdx === null) {
        return false;
      }
      const allWords = [...expected, ...interaction.distractorWords];
      return allWords[wordIdx] === expected[blankIdx];
    });
  }
  if (interaction.type === "info_hint") {
    return true;
  }
  if (!isFillBlankAnswer(answer)) {
    return false;
  }
  const fillData = parseFillBlankData(interaction);
  if (answer.values.length !== fillData.blanks.length) {
    return false;
  }
  return fillData.blanks.every((acceptedList, idx) =>
    matchesFillBlankValue(answer.values[idx] ?? "", acceptedList)
  );
}

function useProjectFromUrl(){
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const data = params.get("data");
    if (!data) {
      return null;
    }
    return decodeProject(data);
  }, []);
}

function parseWordBankTemplate(template) {
  const regex = /\*([^*]+)\*/g;

  const parts = [];
  const correctWords = [];

  let cursor = 0;
  let blankIndex = 0;

  for (const match of template.matchAll(regex)) {
    const full = match[0];
    const word = (match[1] || "").trim();
    const index = match.index || 0;

    if (index > cursor) {
      parts.push({
        type: "text",
        value: template.slice(cursor, index)
      });
    }

    if (word) {
      correctWords.push(word);
      parts.push({
        type: "blank",
        blankIndex: blankIndex
      });
      blankIndex += 1;
    } else {
      parts.push({
        type: "text",
        value: full
      });
    }

    cursor = index + full.length;
  }

  if (cursor < template.length) {
    parts.push({
      type: "text",
      value: template.slice(cursor)
    });
  }

  if (parts.length === 0) {
    parts.push({
      type: "text",
      value: template
    });
  }

  return { parts, correctWords };
}


function getWordBankCorrectWords(interaction){
  if (interaction.correctWords.length > 0) {
    return interaction.correctWords;
  }
  return parseWordBankTemplate(interaction.sentenceTemplate).correctWords;
}

function getSafePosition(interaction) {
  const raw = (interaction ).position;
  const xPct =
    typeof raw?.xPct === "number" && Number.isFinite(raw.xPct) ? raw.xPct : 50;
  const yPct =
    typeof raw?.yPct === "number" && Number.isFinite(raw.yPct) ? raw.yPct : 50;
  const margin = interaction.type === "info_hint" ? 6 : 22;
  return {
    xPct: Math.min(100 - margin, Math.max(margin, xPct)),
    yPct: Math.min(100 - margin, Math.max(margin, yPct))
  };
}

function getPopupPosition(interaction) {
  // Wide interactions should stay centered to avoid clipping in the video area.
  if (interaction.type === "drag_drop" || interaction.type === "word_bank_fill") {
    return { xPct: 50, yPct: 50 };
  }
  return getSafePosition(interaction);
}

function getInteractionMedia(interaction){
  return {
    backgroundImageUrl: interaction.backgroundImageUrl?.trim() ?? "",
    exampleImageUrl: interaction.exampleImageUrl?.trim() ?? ""
  };
}

export function EmbedPlayer() {
  const project = useProjectFromUrl();
  const videoRef = useRef(null);
  const markerSeekRef = useRef(false);
  const playerWrapRef = useRef(null);
  const overlayCardRef = useRef(null);
  const lastPlaybackTimeRef = useRef(0);
  const [currentTimeSec, setCurrentTimeSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [selectedDragItem, setSelectedDragItem] = useState(null);
  const [selectedWordBankItem, setSelectedWordBankItem] = useState(
    null
  );
  const [wordBankPool, setWordBankPool] = useState([]);
  const [mcOptionOrder, setMcOptionOrder] = useState([]);
  const [resolvedPopupPosition, setResolvedPopupPosition] = useState({ xPct: 50, yPct: 50 });



  const sortedInteractions = [...project.interactions].sort((a, b) => a.timeSec - b.timeSec);
  console.log("sortedInteractions",sortedInteractions);
  const blockingInteractions = sortedInteractions.filter(
    (q) => q.type !== "info_hint"
  );
  const activeHints = sortedInteractions.filter(
    (q) =>
      q.type === "info_hint" &&
      currentTimeSec >= q.timeSec &&
      currentTimeSec <= q.timeSec + Math.max(1, Number(q.durationSec || 10))
  );

  const onTimeUpdate = () => {
    const current = videoRef.current?.currentTime ?? 0;
    setCurrentTimeSec(current);
    if (active) {
      lastPlaybackTimeRef.current = current;
      return;
    }
    const previous = lastPlaybackTimeRef.current;
    const pending = blockingInteractions.find(
      (q) => q.timeSec > previous && q.timeSec <= current && !answers[q.id]
    );
    if (pending) {
      setActive(pending);
      setCurrentAnswer(
        pending.type === "multiple_choice"
          ? createEmptyMultipleChoiceAnswer()
          : pending.type === "true_false"
            ? true
            : pending.type === "drag_drop"
              ? createEmptyDragDropAnswer(pending)
              : pending.type === "fill_blank"
                ? createEmptyFillBlankAnswer(pending)
              : pending.type === "word_bank_fill"
                ? createEmptyWordBankFillAnswer(
                    getWordBankCorrectWords(pending).length
                  )
            : ""
      );
      if (pending.type === "word_bank_fill") {
        const correctWords = getWordBankCorrectWords(pending);
        setWordBankPool([...correctWords, ...pending.distractorWords]);
      } else {
        setWordBankPool([]);
      }
      if (pending.type === "multiple_choice") {
        const mc = normalizeMultipleChoice(pending);
        setMcOptionOrder(shuffleIndexes(mc.options.length));
      } else {
        setMcOptionOrder([]);
      }
      setSelectedDragItem(null);
      setSelectedWordBankItem(null);
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }
    lastPlaybackTimeRef.current = current;
  };

  const onSeeked = () => {
    const current = videoRef.current?.currentTime ?? 0;
    const previous = lastPlaybackTimeRef.current;
  
    setCurrentTimeSec(current);
  
    if (markerSeekRef.current) {
      markerSeekRef.current = false;
      lastPlaybackTimeRef.current = current;
      return; // don't close interaction
    }
  
    // original logic
    if (current > previous + 1) {
      setAnswers((prev) => {
        const next = { ...prev };
        blockingInteractions.forEach((q) => {
          if (q.timeSec < current && !next[q.id]) {
            next[q.id] = { isCorrect: false };
          }
        });
        return next;
      });
    }
  
    setActive(null);
    lastPlaybackTimeRef.current = current;
    setSelectedWordBankItem(null);
    setMcOptionOrder([]);
  };
  

  const onSeeking = () => {
    const current = videoRef.current?.currentTime ?? 0;
    lastPlaybackTimeRef.current = current;
    setCurrentTimeSec(current);
  };

  const onCustomSeek = (nextTime) => {
    const safe = Math.max(0, Math.min(durationSec || 0, nextTime));
    if (videoRef.current) {
      videoRef.current.currentTime = safe;
    }
    setCurrentTimeSec(safe);
    lastPlaybackTimeRef.current = safe;
  };

  const submitAnswer = () => {
    if (!active) {
      return;
    }
    if (
      active.type === "multiple_choice" &&
      (!isMultipleChoiceAnswer(currentAnswer) ||
        currentAnswer.selectedOptionIndexes.length === 0)
    ) {
      return;
    }
    const isCorrect = validateAnswer(active, currentAnswer);
    setAnswers((prev) => ({
      ...prev,
      [active.id]: {
        isCorrect
      }
    }));
  };

  const continuePlayback = () => {
    if (active && !answers[active.id]) {
      setAnswers((prev) => ({
        ...prev,
        [active.id]: { isCorrect: false }
      }));
    }
    setActive(null);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  // Result from the ported (web-parity) interaction card. The card owns its own
  // answer/check/reveal/retry state and reports the final correctness here; we
  // just record it and resume playback (mirrors continuePlayback).
  const handleInteractionResult = (isCorrect) => {
    if (active) {
      setAnswers((prev) => ({
        ...prev,
        [active.id]: { isCorrect: Boolean(isCorrect) }
      }));
    }
    setActive(null);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const toggleReveal = () => {
    if (!active) {
      return;
    }
    setRevealed((prev) => ({
      ...prev,
      [active.id]: !prev[active.id]
    }));
  };

  const retryCurrent = () => {
    if (!active) {
      return;
    }
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[active.id];
      return next;
    });
    setCurrentAnswer(
      active.type === "multiple_choice"
        ? createEmptyMultipleChoiceAnswer()
        : active.type === "true_false"
          ? true
          : active.type === "drag_drop"
            ? createEmptyDragDropAnswer(active)
            : active.type === "fill_blank"
              ? createEmptyFillBlankAnswer(active)
            : active.type === "word_bank_fill"
              ? createEmptyWordBankFillAnswer(getWordBankCorrectWords(active).length)
          : ""
    );
    setSelectedDragItem(null);
    setSelectedWordBankItem(null);
    if (active.type === "word_bank_fill") {
      const correctWords = getWordBankCorrectWords(active);
      setWordBankPool([...correctWords, ...active.distractorWords]);
    }
    if (active.type === "multiple_choice") {
      const mc = normalizeMultipleChoice(active);
      setMcOptionOrder(shuffleIndexes(mc.options.length));
    }
  };

  const currentResult = active ? answers[active.id] : undefined;
  const isRevealed = active ? Boolean(revealed[active.id]) : false;
  const activePosition = active ? getPopupPosition(active) : { xPct: 50, yPct: 50 };
  const activeMedia = active ? getInteractionMedia(active) : { backgroundImageUrl: "", exampleImageUrl: "" };
  const mcData = active?.type === "multiple_choice" ? normalizeMultipleChoice(active) : null;
  const mcAnswer = isMultipleChoiceAnswer(currentAnswer)
    ? currentAnswer
    : createEmptyMultipleChoiceAnswer();
  const dragDropAnswer =
    active?.type === "drag_drop"
      ? toSafeDragDropAnswer(currentAnswer, active.slots.length, active.items.length)
      : null;
  const dragDropCorrectBySlot =
    active?.type === "drag_drop" ? normalizeCorrectBySlot(active) : [];
  const usedDragItems = new Set((dragDropAnswer?.assignments ?? []).flat());

  const assignDragItem = (slotIdx, itemIdx) => {
    if (!active || active.type !== "drag_drop" || !dragDropAnswer || currentResult) {
      return;
    }
    const nextAssignments = dragDropAnswer.assignments.map((group) => [...group]);
    const existingSlotIdx = nextAssignments.findIndex((group) =>
      group.includes(itemIdx)
    );
    if (existingSlotIdx >= 0) {
      nextAssignments[existingSlotIdx] = nextAssignments[existingSlotIdx].filter(
        (v) => v !== itemIdx
      );
    }
    if (!nextAssignments[slotIdx].includes(itemIdx)) {
      nextAssignments[slotIdx].push(itemIdx);
    }
    setCurrentAnswer({ assignments: nextAssignments });
    setSelectedDragItem(null);
  };

  const removeDragItemFromSlot = (slotIdx, itemIdx) => {
    if (!active || active.type !== "drag_drop" || !dragDropAnswer || currentResult) {
      return;
    }
    const nextAssignments = dragDropAnswer.assignments.map((group) => [...group]);
    nextAssignments[slotIdx] = nextAssignments[slotIdx].filter((v) => v !== itemIdx);
    setCurrentAnswer({ assignments: nextAssignments });
  };

  const dragDropAssignedCount = dragDropAnswer?.assignments.flat().length ?? 0;
  const dragDropMinRequiredCount =
    dragDropCorrectBySlot.flat().length || 1;
  const dragDropCanCheck = dragDropAssignedCount >= dragDropMinRequiredCount;
  const wordBankAnswer =
    active?.type === "word_bank_fill" && isWordBankFillAnswer(currentAnswer)
      ? currentAnswer
      : null;
  const wordBankTemplate =
    active?.type === "word_bank_fill"
      ? parseWordBankTemplate(active.sentenceTemplate)
      : { parts: [], correctWords: [] };
  const usedWordBankIndexes = new Set(
    (wordBankAnswer?.assignments ?? []).filter(
      (idx) => idx !== null
    )
  );
  const wordBankCanCheck =
    active?.type === "word_bank_fill" &&
    wordBankAnswer?.assignments.every((idx) => idx !== null);

  const assignWordBankToBlank = (blankIdx, wordIdx) => {
    if (
      !active ||
      active.type !== "word_bank_fill" ||
      !wordBankAnswer ||
      currentResult
    ) {
      return;
    }
    const next = [...wordBankAnswer.assignments];
    const existingBlank = next.findIndex((idx) => idx === wordIdx);
    if (existingBlank >= 0) {
      next[existingBlank] = null;
    }
    next[blankIdx] = wordIdx;
    setCurrentAnswer({ assignments: next });
    setSelectedWordBankItem(null);
  };

  const clearWordBankBlank = (blankIdx) => {
    if (
      !active ||
      active.type !== "word_bank_fill" ||
      !wordBankAnswer ||
      currentResult
    ) {
      return;
    }
    const next = [...wordBankAnswer.assignments];
    next[blankIdx] = null;
    setCurrentAnswer({ assignments: next });
  };

  const fillBlankPromptParts =
    active?.type === "fill_blank" ? parseFillBlankData(active) : { parts: [], blanks: [] };
  const fillBlankAnswer =
    active?.type === "fill_blank" && isFillBlankAnswer(currentAnswer)
      ? currentAnswer
      : active?.type === "fill_blank"
        ? createEmptyFillBlankAnswer(active)
        : { values: [] };
  const fillBlankMatches =
    active?.type === "fill_blank"
      ? fillBlankPromptParts.blanks.map((acceptedList, idx) =>
          matchesFillBlankValue(fillBlankAnswer.values[idx] ?? "", acceptedList)
        )
      : [];

      useEffect(() => {
        if (!active || !playerWrapRef.current || !overlayCardRef.current) {
          return;
        }
      
        const basePosition = getPopupPosition(active);
      
        const clampPopupInsideVideo = () => {
          const containerRect = playerWrapRef.current.getBoundingClientRect();
          const cardRect = overlayCardRef.current.getBoundingClientRect();
      
          if (
            containerRect.width <= 0 ||
            containerRect.height <= 0 ||
            cardRect.width <= 0 ||
            cardRect.height <= 0
          ) {
            return;
          }
      
          const margin = 8;
      
          const desiredX = (basePosition.xPct / 100) * containerRect.width;
          const desiredY = (basePosition.yPct / 100) * containerRect.height;
      
          const halfW = cardRect.width / 2;
          const halfH = cardRect.height / 2;
      
          const minX = halfW + margin;
          const maxX = containerRect.width - halfW - margin;
          const minY = halfH + margin;
          const maxY = containerRect.height - halfH - margin;
      
          const clampedX = Math.min(maxX, Math.max(minX, desiredX));
          const clampedY = Math.min(maxY, Math.max(minY, desiredY));
      
          const next = {
            xPct: (clampedX / containerRect.width) * 100,
            yPct: (clampedY / containerRect.height) * 100
          };
      
          setResolvedPopupPosition((prev) => {
            if (
              Math.abs(prev.xPct - next.xPct) < 0.1 &&
              Math.abs(prev.yPct - next.yPct) < 0.1
            ) {
              return prev;
            }
            return next;
          });
        };
      
        clampPopupInsideVideo();
      
        window.addEventListener("resize", clampPopupInsideVideo);
      
        return () => {
          window.removeEventListener("resize", clampPopupInsideVideo);
        };
      }, [active]);
      

  if (!project) {
    return (
      <div className="video-builder-scope"> 
      <div className="layout">
        <section className="card">
          <h1>Geçersiz gömme verisi</h1>
          <p>URL parametresindeki proje verisi eksik veya hatalı.</p>
        </section>
      </div>
      </div>
    );
  }

  if (!project.videoUrl.trim()) {
    return (
      <div className="video-builder-scope"> 
      <div className="layout">
        <section className="card">
          <h1>Video URL ayarlanmamış</h1>
          <p>
            Bu gömme verisinde <code>videoUrl</code> boş olduğu için video
            oynatılamıyor.
          </p>
          <p className="muted">
            Oluşturucu sayfasına dönüp gerçek bir `.mp4/.webm` URL girin ve
            yeniden dışa aktarın.
          </p>
        </section>
      </div>
      </div>
    );
  }

  const openInteraction = (interaction) => {
    if (!interaction) return;
  
    setActive(interaction);
  
    setCurrentAnswer(
      interaction.type === "multiple_choice"
        ? createEmptyMultipleChoiceAnswer()
        : interaction.type === "true_false"
          ? true
          : interaction.type === "drag_drop"
            ? createEmptyDragDropAnswer(interaction)
            : interaction.type === "fill_blank"
              ? createEmptyFillBlankAnswer(interaction)
              : interaction.type === "word_bank_fill"
                ? createEmptyWordBankFillAnswer(
                    getWordBankCorrectWords(interaction).length
                  )
                : ""
    );
  
    if (interaction.type === "word_bank_fill") {
      const correctWords = getWordBankCorrectWords(interaction);
      setWordBankPool([...correctWords, ...(interaction.distractorWords || [])]);
    } else {
      setWordBankPool([]);
    }
  
    if (interaction.type === "multiple_choice") {
      const mc = normalizeMultipleChoice(interaction);
      setMcOptionOrder(shuffleIndexes(mc.options.length));
    } else {
      setMcOptionOrder([]);
    }
  
    setSelectedDragItem(null);
    setSelectedWordBankItem(null);
  
    // pause video so interaction is usable
    if (videoRef.current) videoRef.current.pause();
  };
  
  const seekAndOpenInteraction = (interaction) => {
    if (!interaction) return;
  
    const t = Math.max(0, Number(interaction.timeSec || 0));
  
    markerSeekRef.current = true;
  
    if (videoRef.current) {
      videoRef.current.currentTime = t;
      videoRef.current.pause();
    }
  
    setCurrentTimeSec(t);
    lastPlaybackTimeRef.current = Math.max(0, t - 0.05);
  
    openInteraction(interaction);
  };
  
  

  return (
    <div className="video-builder-scope">

    <div className="embed-layout">
      <div className="player-wrap" ref={playerWrapRef}>
        <video
          ref={videoRef}
          controls
          src={project.videoUrl}
          onTimeUpdate={onTimeUpdate}
          onLoadedMetadata={(e) => setDurationSec(e.currentTarget.duration || 0)}
          onDurationChange={(e) => setDurationSec(e.currentTarget.duration || 0)}
          onSeeking={onSeeking}
          onSeeked={onSeeked}
          className="video"
        />
        <div className="video-marker-layer">
  {sortedInteractions.map((interaction) => {
    const leftPct =
      durationSec > 0
        ? (interaction.timeSec / durationSec) * 100
        : 0;

    return (
      <button
        key={`marker_${interaction.id}`}
        className={`video-marker type-${interaction.type}`}
        style={{ left: `${leftPct}%` }}
        onClick={() => seekAndOpenInteraction(interaction)}
        title={`${interaction.timeSec.toFixed(1)}s`}
      />
    );
  })}
</div>

        {/* <div className="interaction-timeline">
          <input
            type="range"
            min={0}
            max={durationSec || 0}
            step={0.1}
            value={Math.min(currentTimeSec, durationSec || currentTimeSec)}
            onChange={(e) => onCustomSeek(Number(e.target.value))}
            className="timeline-range"
          />
          <div className="timeline-markers">
            {sortedInteractions.map((interaction) => {
              const leftPct =
                durationSec > 0
                  ? Math.min(100, Math.max(0, (interaction.timeSec / durationSec) * 100))
                  : 0;
              return (
                <button
                  key={`marker_${interaction.id}`}
                  className={`timeline-marker type-${interaction.type}`}
                  style={{ left: `${leftPct}%` }}
                  title={`${interaction.timeSec.toFixed(0)}s - ${interaction.type}`}
                  onClick={() => onCustomSeek(interaction.timeSec)}
                />
              );
            })}
          </div>
        </div> */}
        {activeHints.length > 0 && (
          <div className="hint-layer">
            {activeHints.map((hint) => (
              <div
                className="hint-card"
                key={hint.id}
                style={{
                  left: `${getSafePosition(hint).xPct}%`,
                  top: `${getSafePosition(hint).yPct}%`,
                  ...(getInteractionMedia(hint).backgroundImageUrl
                    ? {
                        backgroundImage: `linear-gradient(rgba(15,23,42,0.82), rgba(15,23,42,0.86)), url(${getInteractionMedia(hint).backgroundImageUrl})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center"
                      }
                    : {})
                }}
              >
                <strong>Bilgi</strong>
                {getInteractionMedia(hint).exampleImageUrl && (
                  <img
                    src={getInteractionMedia(hint).exampleImageUrl}
                    alt="İpucu görseli"
                    className="interaction-example-image hint-example"
                  />
                )}
                <p>
                  <FormattedText text={hint.prompt} />
                </p>
              </div>
            ))}
          </div>
        )}

        {active && (
          <div className="overlay" style={{ background: "transparent" }}>
            <InteractionOverlay
              interaction={active}
              onResult={handleInteractionResult}
            />
          </div>
        )}
      </div>
    </div>
    </div>

  );
}
